import { useCallback, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { datadogRum } from '@datadog/browser-rum'

import { type AsyncInvokeAgentPartialResult, type AsyncInvokeAgentProgressSnapshot, AiUseCase } from '@einwert/valuation-api-client'
import { ValXmlExtractor, type ValXmlWorkflowInvoker } from 'ComponentLibrary/src/playground'

import { useInvokeWorkflow } from 'ClientPortal/src/api/Queries/ai'
import { usePlaygroundFeedback } from './usePlaygroundFeedback'
import { usePlaygroundTranslations } from './usePlaygroundTranslations'

const FEEDBACK_MODAL_DELAY_MS = 5000

export const ValXmlExtractorPage = () => {
    const location = useLocation()
    const { feedbackButton, openFeedback } = usePlaygroundFeedback()
    const { dashboardBreadcrumbRoot, valXmlExtractorCopy } = usePlaygroundTranslations()
    const feedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
    const workflowCallbacksRef = useRef<Pick<Parameters<ValXmlWorkflowInvoker>[0], 'onProgress' | 'onPartialResult'>>({})

    const eventContext = {
        app: 'client_portal',
        toolKey: 'val-xml-extractor',
        toolTitle: valXmlExtractorCopy.title,
        path: location.pathname,
    }

    useEffect(() => {
        datadogRum.addAction('playground_tool_visited', eventContext)

        return () => {
            datadogRum.addAction('playground_tool_left', eventContext)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname])

    useEffect(
        () => () => {
            if (feedbackTimeoutRef.current) {
                clearTimeout(feedbackTimeoutRef.current)
            }
        },
        []
    )

    const forwardWorkflowProgress = useCallback((progress: AsyncInvokeAgentProgressSnapshot) => {
        workflowCallbacksRef.current.onProgress?.(progress)
    }, [])
    const forwardWorkflowPartialResult = useCallback((partialResult: AsyncInvokeAgentPartialResult) => {
        workflowCallbacksRef.current.onPartialResult?.(partialResult)
    }, [])
    const { mutateAsync: invokeExtraction } = useInvokeWorkflow({
        agentUseCase: AiUseCase.VALXML_EXTRACTOR,
        onProgress: forwardWorkflowProgress,
        onPartialResult: forwardWorkflowPartialResult,
    })
    const { mutateAsync: invokeReview } = useInvokeWorkflow({
        agentUseCase: AiUseCase.VALXML_EXTRACTOR,
        onProgress: forwardWorkflowProgress,
        onPartialResult: forwardWorkflowPartialResult,
    })
    const invokeWorkflow = useCallback<ValXmlWorkflowInvoker>(
        async ({ phase, onProgress, onPartialResult, ...request }) => {
            workflowCallbacksRef.current = { onProgress, onPartialResult }
            const isReview = phase === 'review'
            try {
                return await (isReview ? invokeReview(request) : invokeExtraction(request))
            } finally {
                workflowCallbacksRef.current = {}
            }
        },
        [invokeExtraction, invokeReview]
    )
    const openFeedbackAfterDownload = useCallback(() => {
        if (feedbackTimeoutRef.current) {
            clearTimeout(feedbackTimeoutRef.current)
        }

        feedbackTimeoutRef.current = setTimeout(() => {
            openFeedback()
            feedbackTimeoutRef.current = undefined
        }, FEEDBACK_MODAL_DELAY_MS)
    }, [openFeedback])

    return (
        <ValXmlExtractor
            invokeWorkflow={invokeWorkflow}
            breadcrumbRoot={dashboardBreadcrumbRoot}
            copy={valXmlExtractorCopy}
            feedbackButton={feedbackButton}
            onValXmlDownloaded={openFeedbackAfterDownload}
            onDataPrivacyModalOpen={() => datadogRum.addAction('ai_tool_data_privacy_modal_opened', eventContext)}
            onInfoModalOpen={() => datadogRum.addAction('ai_tool_info_modal_opened', eventContext)}
            onSamplePdfUsed={() => datadogRum.addAction('ai_tool_sample_pdf_used', eventContext)}
        />
    )
}
